import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { SelectFieldComponent } from '../../../../components/form/select-field/select-field.component';
import { AlertInfo } from '../../../../models/alert-info';
import { SavedDataInfo } from '../../../../models/form/saved-data-info';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { CustomerService } from '../../../../services/api/customer.service';
import { FormService } from '../../../../services/form.service';

@Component({
  selector: 'customers-welcome-letter',
  imports: [CommonModule, ReactiveFormsModule, NgbAlert, SelectFieldComponent],
  templateUrl: './customers-welcome-letter.component.html',
  styleUrl: './customers-welcome-letter.component.scss'
})
export class CustomersWelcomeLetterComponent implements OnInit {
  @Input() readonlyWelcomeLetter: boolean = false;
  @Input() customer: any;
  @Input() clientPersons: any[] = [];
  @Input() personSentWelcomeLetter: any[] = [];
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  form!: FormGroup;
  alertInfo: AlertInfo = { show: false, type: '', message: '' };
  recipientOptions: { key: any; value: string }[] = [];
  welcomeLetterSent: boolean = false;
  sentDate: string = '';

  constructor(
    private formService: FormService,
    private alertNotificationService: AlertNotificationService,
    private customerService: CustomerService
  ) {
    this.form = new FormGroup({
      customerPersonId: new FormControl('', [Validators.required]),
      customerId: new FormControl('')
    });
  }

  ngOnInit(): void {
    if (this.clientPersons && this.clientPersons.length > 0) {
      this.prepareRecipientOptions();
    }
    
    if (this.personSentWelcomeLetter && this.personSentWelcomeLetter.length > 0) {
      this.welcomeLetterSent = true;
      this.checkWelcomeLetterData();
    }
    
    if (this.customer && this.customer.customerId) {
      this.form.get('customerId')?.setValue(this.customer.customerId);
    }
  }

  ngOnChanges(): void {
    if (this.clientPersons && this.clientPersons.length > 0) {
      this.prepareRecipientOptions();
    }
    
    if (this.personSentWelcomeLetter && this.personSentWelcomeLetter.length > 0) {
      this.welcomeLetterSent = true;
      this.checkWelcomeLetterData();
    }
    
    if (this.customer && this.customer.customerId) {
      this.form.get('customerId')?.setValue(this.customer.customerId);
    }
  }
  
  checkWelcomeLetterData(): void {
    if (this.personSentWelcomeLetter && this.personSentWelcomeLetter.length > 0) {
      const sample = this.personSentWelcomeLetter[0];
      if (!sample.customerPerson) {
        console.warn('La struttura dei dati non corrisponde: customerPerson mancante');
      }
      if (!sample.recipient) {
        console.warn('La struttura dei dati non corrisponde: recipient mancante');
      }
      if (!sample.createTime) {
        console.warn('La struttura dei dati non corrisponde: createTime mancante');
      }
    }
  }

  prepareRecipientOptions(): void {    
    this.recipientOptions = this.clientPersons.map(person => ({
      key: person.customerPersonId,
      value: `${person.firstName} ${person.lastName} (${person.email})`
    }));
  }

  getRecipientSelect(): FormControl {
    return this.form.get('customerPersonId') as FormControl;
  }

  sendWelcomeLetter(): void {
    if (this.form.valid) {
      const customerPersonId = this.form.get('customerPersonId')?.value;
      const customerId = this.form.get('customerId')?.value || this.customer.customerId;
      
      if (!customerPersonId) {
        this.showAlert('danger', 'Please select a recipient');
        return;
      }
      
      if (!customerId) {
        this.showAlert('danger', 'Customer ID is missing');
        return;
      }

      this.alertInfo.show = false;
      
      this.customerService.createWelcomeLetters(customerId, customerPersonId, {customerId: customerId, customerPersonId: customerPersonId}).subscribe({
        next: (response) => {
          if (response.status && response.status.code === 200) {
            this.welcomeLetterSent = true;
            this.sentDate = new Date().toISOString().split('T')[0];
            
            
            const newWelcomeLetter = {
              customerWelcomeLetterSentId: response.data?.customerWelcomeLetterSentId || Date.now(),
              customerPerson: {
                customerPersonId: customerPersonId,
                firstName: this.getRecipientName().split(' ')[0],
                lastName: this.getRecipientName().split(' ').slice(1).join(' ')
              },
              customerWelcomeLetter: {
                customerWelcomeLetterId: response.data?.customerWelcomeLetterId || Date.now()
              },
              recipient: this.getRecipientEmail(),
              createTime: new Date().toISOString()
            };
            
            if (!this.personSentWelcomeLetter) {
              this.personSentWelcomeLetter = [];
            }
            
            this.personSentWelcomeLetter.push(newWelcomeLetter);
            
            this.showAlert('success', 'Welcome letter sent successfully');
            this.alertNotificationService.success('Welcome letter sent successfully');
            
            this.onSavedData.emit({
              success: true,
              response: newWelcomeLetter
            });
          } else {
            this.showAlert('danger', 'Error sending welcome letter: Invalid response');
            this.alertNotificationService.error('Error sending welcome letter');
          }
        },
        error: (error) => {
          this.showAlert('danger', 'Error sending welcome letter: ' + (error.message || 'Unknown error'));
          this.alertNotificationService.error('Error sending welcome letter');
        }
      });
    } else {
      this.showAlert('danger', 'Please select a recipient');
    }
  }
  
  getRecipientName(): string {
    const customerPersonId = this.form.value.customerPersonId;
    const selectedOption = this.recipientOptions.find(option => option.key == customerPersonId);
    if (selectedOption && selectedOption.value) {
      const parts = selectedOption.value.split('(');
      return parts[0].trim();
    }
    return '';
  }
  
  getRecipientEmail(): string {
    const customerPersonId = this.form.value.customerPersonId;
    const selectedOption = this.recipientOptions.find(option => option.key == customerPersonId);
    if (selectedOption && selectedOption.value) {
      const parts = selectedOption.value.split('(');
      if (parts.length > 1) {
        return parts[1].replace(')', '').trim();
      }
    }
    return '';
  }

  showAlert(type: string, message: string): void {
    this.alertInfo = {
      show: true,
      type,
      message
    };

    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  
  getFullName(letter: any): string {
    if (letter && letter.customerPerson) {
      return `${letter.customerPerson.firstName || ''} ${letter.customerPerson.lastName || ''}`;
    }
    return '';
  }
}
